import React, { useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as actions from '../../../../store/actions';
import { RootState } from '../../../../store/types';
import weekDayToText from '../../../../utils/weekDayToText';
import monthToText from '../../../../utils/monthToText';
import moment from 'moment';
import { i18n } from '../../../../translate/i18n';
import Spinner from '../../../../components/Spinner';
import confirm from '../../../../UI/Confirm/confirm';

type Props = {
  redirect: CallableFunction
}

const ScheduledServicesByUserAndDayModal: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const openModal = useSelector((state: RootState) => state.scheduledServices.openModal);
  const showCompletedOnly = useSelector((state: RootState) => state.scheduledServices.showCompletedOnly);
  const servicesByUserAndDay = useSelector((state: RootState) => state.scheduledServices.servicesByUserAndDay);
  const userId = useSelector((state:RootState) => state.scheduledServices.selectedUserId);
  const date = useSelector((state:RootState) => state.scheduledServices.selectedDate);
  const loadingDelete = useSelector((state: RootState) => state.scheduledServices.loadingSoftDeleteService);
  const loadingByUserAndDay = useSelector((state: RootState) => state.scheduledServices.loadingGetByUserAndDay);

  const filteredServicesByUserAndDay = servicesByUserAndDay.filter((service) => service.completed === showCompletedOnly);
  const loading = loadingDelete || loadingByUserAndDay;

  const closeModal = () => {
    dispatch(actions.scheduledServicesOpenCloseServicesByUserAndDayModal());
  }
  
  const formatDateText = (date: Date) => {
    const day = date.getDate();
    const month = monthToText(date.getMonth())
    const year = date.getFullYear();

    return `${i18n.t('titleHeaderEvent.text')} ${day}, ${month}, ${year}`;
  }

  const formatWeekDay = (date: Date) => {
    const weekDay = weekDayToText(moment(date).day());
    return weekDay;
  }

  const handleDeleteScheduledService = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, scheduledServiceId: string) => {
    event.stopPropagation();
    confirm(i18n.t('scheduledServicesByUserAndDayModal.confirm'),
      {
        title: i18n.t('scheduledServicesByUserAndDayModal.title'),
        titleColor: 'red',
        disableBackdrop: true
      })
      .then(
        () => {
          console.log('rejeitado');
        },
        async () => {
          console.log('confirmado');
          deleteScheduledService(scheduledServiceId);
        },
      )
  }

  const deleteScheduledService = (scheduledServiceId: string, ) => {
    
    dispatch(actions.softDeleteScheduledService(scheduledServiceId, userId, date));
  }

  const modalData = showCompletedOnly ? {
    color: 'green',
    title: i18n.t('scheduledServicesByUserAndDayModal.titleCompleted'),
  } : {
    color: '#0288D1',
    title: i18n.t('scheduledServicesByUserAndDayModal.titleScheduled'),
  };

  const ModalHeader: React.FC = () => {
    return (
      <Box sx={styles.headerContainer}>
        <EventAvailableIcon sx={{ color: modalData.color }} fontSize='medium' />
        <Box sx={styles.headerTextContainer}>
          <Box sx={styles.dateText}>{formatDateText(new Date(date))}</Box>
          <Box sx={{ ...styles.dateText, marginTop: '-10px' }}>{formatWeekDay(new Date(date))}</Box>
          <Box sx={{ ...styles.titleText, color: modalData.color }}>{modalData.title as string}</Box>
        </Box>
      </Box>
    );
  }

  useEffect(() => {
    if (openModal && userId !== "" && date !== "") dispatch(actions.fetchScheduledServicesByUserAndDay(userId, date));
  }, [userId, date, openModal]);

  const goToEquipmentServices = (equipmentId: string) => props.redirect(equipmentId);

  return (
    <Drawer
      anchor='right'
      open={openModal}
      onClose={closeModal}
      classes={{
        paper: classes.paper
      }}
    >
      <Box sx={styles.modalContainer}>
        { loading && <Spinner overlay size={40} />}
        <Box sx={styles.closeIconContainer}>
          <CloseIcon style={styles.closeIcon} onClick={closeModal}/>
        </Box>
        <ModalHeader />
        {filteredServicesByUserAndDay.map((service) => <Button key={service._id} sx={{...styles.cardContainer, borderLeftColor: modalData.color}} onClick={() => goToEquipmentServices(service.equipment._id)}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
            <Typography>{service.equipmentModelName}</Typography>
            <IconButton children={<DeleteRounded/>} onClick={(event) => handleDeleteScheduledService(event, service._id)}/>
          </Box>
          <Typography>{`${service.serviceType.name} ${service.frequency.name}`}</Typography>
          <Box sx={styles.dataContainer}>
            <Box sx={styles.label}>{i18n.t('scheduledServicesByUserAndDayModal.serialNumber') as string}</Box>
            <Box sx={styles.data}>{service.equipment.serialNumber}</Box>
          </Box>
          <Box sx={styles.dataContainer}>
            <Box sx={styles.label}>{i18n.t('scheduledServicesByUserAndDayModal.place') as string}</Box>
            <Box sx={styles.data}>{service.place.path}</Box>
          </Box>
          <Box sx={styles.dataContainer}>
            <Box sx={styles.label}>{i18n.t('scheduledServicesByUserAndDayModal.scheduledDate') as string}</Box>
            <Box sx={styles.data}>{new Date(service.scheduledDate).toLocaleDateString('pt-BR')}</Box>
          </Box>
          <Box sx={styles.dataContainer}>
            <Box sx={styles.label}>{i18n.t('scheduledServicesByUserAndDayModal.accountable') as string}</Box>
            <Box sx={styles.data}>{service.accountables[0].name}</Box>
          </Box>
        </Button>)}
      </Box>
    </Drawer>
  );
}

const useStyles = makeStyles({
  paper: {
    backgroundColor: '#F5F5F5',
    overflowY: 'scroll',
    overflowX: 'hidden'
  },
});

const styles = {
  modalContainer: {
    width: '400px',
    backgroundColor: '#F5F5F5',
    position: 'relative',
    paddingTop: '50px',
    paddingLeft: '30px',
    paddingRight: '20px',
  },
  closeIconContainer: {
    position: 'absolute',
    top: '10px',
    left: '15px',
  },
  closeIcon: {
   fontSize: '30px',
   cursor: 'pointer',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginLeft: '5px',
    marginBottom: '40px'
  },
  headerTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '10px'
  },
  dateText: {
    fontSize: '1.5rem'
  },
  titleText: {
    marginTop: "-5px",
    fontSize: '1rem',
  },
  cardContainer: {
    backgroundColor: "#fff",
    padding: "15px",
    borderLeftWidth: "5px",
    borderLeftStyle: "solid",
    marginBottom: "20px",
    paddingTop: '7px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '360px'
  },
  descriptionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  label: {
    color: "#828282",
    fontSize: "0.8rem"
  },
  dataContainer: {
    marginTop: "5px",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: '300px'
  },
  data: {
    marginTop: "-3px",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    direction: 'rtl',
    textAlign: 'left',
    maxWidth: '320px'
  },
}

export default ScheduledServicesByUserAndDayModal;

